import type { ApiResponse } from '@safepass/shared';
import { endSession, isAuthPath, refreshAccessToken } from './session';

const BASE_URL = '/api/v1';

class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function getAuthHeader(): Record<string, string> {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  options?: RequestInit
): Promise<T> {
  const send = () =>
    fetch(`${BASE_URL}${path}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
        ...options?.headers,
      },
      credentials: 'include',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });

  let res = await send();

  /*
    An expired access token is renewed once, invisibly, and the request retried.
    If it cannot be renewed the session is genuinely over and the person is
    signed out rather than left on a page where nothing works. See lib/session.
  */
  if (res.status === 401 && !isAuthPath(path)) {
    const token = await refreshAccessToken();
    if (token) {
      res = await send();
      if (res.status === 401) endSession();
    } else {
      endSession();
    }
  }

  const data = (await res.json()) as ApiResponse<T>;

  if (!data.success) {
    throw new ApiError(res.status, data.error.code, data.error.message, data.error.details);
  }

  return (data as { success: true; data: T }).data;
}

export const api = {
  get: <T>(path: string, options?: RequestInit) => request<T>('GET', path, undefined, options),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};

export { ApiError };

// ─── Auth ─────────────────────────────────────────────────────────────────────

export interface LoginResult {
  accessToken: string;
  user: {
    id: string;
    role: string;
    fullName?: string;
    email?: string;
    institutionId?: string;
  };
}

export const authApi = {
  adminLogin: (email: string, password: string) =>
    api.post<LoginResult>('/auth/admin/login', { email, password }),
  refresh: () => api.post<{ accessToken: string }>('/auth/refresh'),
  logout: () => api.post<null>('/auth/logout'),
  getMe: () => api.get<LoginResult['user']>('/auth/me'),
};

// ─── Institutions ─────────────────────────────────────────────────────────────

export const institutionsApi = {
  getPickupBoard: (institutionId: string) =>
    api.get<{
      institutionId: string;
      date: string;
      total: number;
      released: number;
      pending: number;
      flagged: number;
    }>(`/institutions/${institutionId}/pickup-board`),
  getAuditLog: (
    institutionId: string,
    params: {
      page?: number;
      pageSize?: number;
      studentId?: string;
      eventType?: string;
      from?: string;
      to?: string;
    }
  ) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.studentId) qs.set('studentId', params.studentId);
    if (params.eventType) qs.set('eventType', params.eventType);
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return api.get<{
      items: AuditEvent[];
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    }>(`/institutions/${institutionId}/audit-log?${qs}`);
  },
  exportReport: (institutionId: string, date: string, format: 'csv') =>
    fetch(`${BASE_URL}/institutions/${institutionId}/reports/daily?date=${date}&format=${format}`, {
      headers: getAuthHeader(),
    }),
  updateSettings: (institutionId: string, data: Partial<InstitutionSettings>) =>
    api.patch<InstitutionSettings>(`/institutions/${institutionId}/settings`, data),
};

// ─── Students ─────────────────────────────────────────────────────────────────

export interface StudentSummary {
  _id: string;
  fullName: string;
  class: string;
  status: string;
  dateOfBirth: string;
  primaryCollectorPhone: string;
  photoS3Key?: string;
}

export const studentsApi = {
  list: (
    institutionId: string,
    params: { page?: number; pageSize?: number; class?: string; status?: string; search?: string }
  ) => {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', String(params.page));
    if (params.pageSize) qs.set('pageSize', String(params.pageSize));
    if (params.class) qs.set('class', params.class);
    if (params.status) qs.set('status', params.status);
    if (params.search) qs.set('search', params.search);
    return api.get<{ items: StudentSummary[]; total: number; page: number; pageSize: number; totalPages: number }>(
      `/institutions/${institutionId}/students?${qs}`
    );
  },
  getById: (institutionId: string, studentId: string) =>
    api.get<StudentSummary & { collectors: unknown[] }>(`/institutions/${institutionId}/students/${studentId}`),
  create: (institutionId: string, data: CreateStudentData) =>
    api.post<StudentSummary>(`/institutions/${institutionId}/students`, data),
  update: (institutionId: string, studentId: string, data: Partial<CreateStudentData>) =>
    api.patch<StudentSummary>(`/institutions/${institutionId}/students/${studentId}`, data),
  bulkImport: (institutionId: string, rows: CreateStudentData[]) =>
    api.post<{ created: number; errors: Array<{ row: number; message: string }> }>(
      `/institutions/${institutionId}/students/bulk`,
      { rows }
    ),
};

export interface CreateStudentData {
  fullName: string;
  dateOfBirth: string;
  class: string;
  primaryCollectorPhone: string;
  status?: string;
}

// ─── Collectors ────────────────────────────────────────────────────────────────

export interface CollectorSummary {
  _id: string;
  fullName: string;
  relationship: string;
  phoneE164: string;
  idType: string;
  isPrimary: boolean;
  isActive: boolean;
}

export const collectorsApi = {
  list: (studentId: string) => api.get<CollectorSummary[]>(`/students/${studentId}/collectors`),
  create: (studentId: string, data: Omit<CollectorSummary, '_id' | 'isActive'>) =>
    api.post<CollectorSummary>(`/students/${studentId}/collectors`, data),
  update: (studentId: string, collectorId: string, data: Partial<CollectorSummary>) =>
    api.patch<CollectorSummary>(`/students/${studentId}/collectors/${collectorId}`, data),
  deactivate: (studentId: string, collectorId: string) =>
    api.delete<void>(`/students/${studentId}/collectors/${collectorId}`),
};

// ─── Delegates ────────────────────────────────────────────────────────────────

export const delegatesApi = {
  list: (studentId: string) =>
    api.get<DelegateSummary[]>(`/students/${studentId}/delegates`),
  add: (studentId: string, data: AddDelegateData) =>
    api.post<DelegateSummary>(`/students/${studentId}/delegates`, data),
  cancel: (studentId: string, delegateId: string) =>
    api.delete<void>(`/students/${studentId}/delegates/${delegateId}`),
};

export interface DelegateSummary {
  _id: string;
  fullName: string;
  phoneE164: string;
  relationship: string;
  type: string;
  isActive: boolean;
  validDate?: string;
}

export interface AddDelegateData {
  fullName: string;
  phoneE164: string;
  relationship: string;
  type: 'recurring' | 'one_day' | 'scheduled';
  validDate?: string;
}

// ─── Staff / Gate Officers ────────────────────────────────────────────────────

export interface StaffMember {
  id: string;
  fullName: string;
  email: string;
  role: 'gate_officer' | 'institution_admin';
  isActive: boolean;
  createdAt: string;
}

export interface CreateStaffData {
  fullName: string;
  email: string;
  password: string;
  role: 'gate_officer' | 'institution_admin';
}

export const staffApi = {
  list: (institutionId: string) =>
    api.get<StaffMember[]>(`/institutions/${institutionId}/staff`),
  create: (institutionId: string, data: CreateStaffData) =>
    api.post<StaffMember>(`/institutions/${institutionId}/staff`, data),
  deactivate: (institutionId: string, userId: string) =>
    api.delete<null>(`/institutions/${institutionId}/staff/${userId}`),
};

// ─── Shared types ─────────────────────────────────────────────────────────────

export interface AuditEvent {
  _id: string;
  studentId: string;
  institutionId: string;
  eventType: string;
  gateOfficerId?: string;
  deviceId?: string;
  isOfflineEvent: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface InstitutionSettings {
  codeGenerationTime: string;
  endOfDayAlertTime: string;
  smsProviderPreference: string;
  offlineModeEnabled: boolean;
}
